import { useEffect, useRef, useState } from 'react';
import { Button } from '@sinohealth/butterfly-ui-components/lib';
import Wave from '@/common/components/BasicLogin/components/Wave';
import { sendSecuritySms, verifySecuritySms } from './api';
import { readLoginState, updateLoginState } from './storage';
import styles from './index.less';

export default function SmsVerification({
  token,
  expireSeconds,
  onVerified,
  onLogout,
}: {
  token: string;
  expireSeconds: number;
  onVerified: () => void;
  onLogout: () => void;
}) {
  const [captcha, setCaptcha] = useState('');
  const [expiresAt, setExpiresAt] = useState(() => readLoginState(token).smsExpiresAt || 0);
  const [now, setNow] = useState(Date.now());
  const [sending, setSending] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [error, setError] = useState('');
  const busy = useRef(false);
  const started = useRef(false);
  const inputs = useRef<Array<HTMLInputElement | null>>([]);
  const active = useRef(true);
  const remaining = Math.max(0, Math.ceil((expiresAt - now) / 1000));

  const send = async () => {
    if (busy.current || expiresAt > Date.now()) return;
    busy.current = true;
    setSending(true);
    setError('');
    try {
      await sendSecuritySms(token);
      if (!active.current) return;
      const deadline = Date.now() + expireSeconds * 1000;
      updateLoginState(token, { smsExpiresAt: deadline });
      setExpiresAt(deadline);
      setNow(Date.now());
      setCaptcha('');
      inputs.current[0]?.focus();
    } catch (err: any) {
      if (active.current) setError(err?.errMessage || err?.message || '验证码发送失败，请重试');
    } finally {
      busy.current = false;
      if (active.current) setSending(false);
    }
  };
  useEffect(() => {
    active.current = true;
    if (!started.current && !readLoginState(token).smsExpiresAt) {
      started.current = true;
      send();
    }
    const timer = window.setInterval(() => setNow(Date.now()), 1000);
    return () => {
      active.current = false;
      window.clearInterval(timer);
    };
  }, []);

  async function verify(event: React.FormEvent) {
    event.preventDefault();
    if (busy.current || verifying || captcha.length !== 6) return;
    busy.current = true;
    setVerifying(true);
    setError('');
    try {
      await verifySecuritySms(token, captcha);
      if (active.current) onVerified();
    } catch (err: any) {
      if (active.current) setError(err?.errMessage || err?.message || '验证失败，请重试');
    } finally {
      busy.current = false;
      if (active.current) setVerifying(false);
    }
  }
  function enter(value: string, index: number) {
    const digits = value.replace(/\D/g, '');
    if (!digits) {
      setCaptcha(captcha.slice(0, index));
      return;
    }
    const next = (captcha.slice(0, index) + digits + captcha.slice(index + digits.length)).slice(
      0,
      6,
    );
    setCaptcha(next);
    inputs.current[Math.min(index + digits.length, 5)]?.focus();
  }
  let sendDescription = '请重新获取验证码';
  if (sending) sendDescription = '正在向您绑定的手机号发送验证码';
  if (expiresAt) sendDescription = '验证码已发送至您绑定的手机号';
  let sendLabel = remaining > 0 ? `重新获取（${remaining}s）` : '重新获取';
  if (sending) sendLabel = '发送中…';
  return (
    <main className={styles.smsPage}>
      <header className={styles.brand}>
        <strong>全病程管理服务平台</strong>
        <small>Whole Course Management Service Platform</small>
      </header>
      <button className={styles.logout} type="button" onClick={onLogout}>
        返回登录
      </button>
      <section className={styles.smsCard}>
        <h1>短信验证码</h1>
        <p>
          {sendDescription}
          <br />
          请在 {expireSeconds} 秒内完成验证
        </p>
        <form onSubmit={verify}>
          <div
            className={styles.digits}
            onPaste={(event) => {
              event.preventDefault();
              enter(event.clipboardData.getData('text'), 0);
            }}
          >
            {Array.from({ length: 6 }, (_, index) => (
              <input
                key={index}
                ref={(el) => {
                  inputs.current[index] = el;
                }}
                aria-label={`第${index + 1}位验证码`}
                inputMode="numeric"
                pattern="[0-9]*"
                autoComplete={index === 0 ? 'one-time-code' : 'off'}
                value={captcha[index] || ''}
                disabled={verifying || sending}
                onChange={(event) => enter(event.target.value, index)}
                onKeyDown={(event) => {
                  if (event.key === 'Backspace') {
                    event.preventDefault();
                    setCaptcha(captcha.slice(0, captcha[index] ? index : Math.max(0, index - 1)));
                    inputs.current[Math.max(0, index - 1)]?.focus();
                  }
                }}
              />
            ))}
          </div>
          <div className={styles.resend}>
            <Button type="link" disabled={remaining > 0 || sending || verifying} onClick={send}>
              {sendLabel}
            </Button>
          </div>
          <div className={styles.error} role="alert">
            {error}
          </div>
          <Button
            type="primary"
            htmlType="submit"
            block
            loading={verifying}
            disabled={captcha.length !== 6 || sending || !expiresAt}
          >
            验 证
          </Button>
        </form>
        <Button type="link" className={styles.back} onClick={onLogout}>
          ← 返回上一步
        </Button>
      </section>
      <Wave />
    </main>
  );
}
